import './calendar.css';
import { cn, getColorForText } from '@/lib/utils';
import { CalendarX2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@/components/Chip';
import { Tone } from '@gokrew/models/ui.types';
import { TranslationNS } from '@gokrew/models/constants';
import {
  CompanyLeaveStatus,
  type CalendarLeave,
} from '@gokrew/models/leave-management';
import type { CalendarDateSpanRange } from '@/types/leave-calendar';
import { AVATAR_COLORS } from '@/constants/avatar-colors';
import {
  formatDate,
  getNameFromObj,
  getHalfDaySessionLabel,
} from '@/utility/utility';
import { getLeaveStatusLabel } from '@/utility/employeeLeaveRequest';
import { STATUS_TONE } from '@/utility/leaveStatus';
import styles from './EventDetailCard.module.css';

interface EventDetailsCardProps {
  date: Date | string;
  badgeClass?: string;
  leave?: CalendarLeave;
  /** Date range of the whole leave request, when the event carries one. */
  range?: CalendarDateSpanRange;
}

export function EventDetailCard({
  leave,
  date,
  range,
  badgeClass,
}: EventDetailsCardProps) {
  const { t, i18n } = useTranslation(TranslationNS.LeaveManagement);

  const formatRangeDate = (value: Date | string) =>
    formatDate(value, { format: 'medium', locale: i18n.language });

  if (!leave) return null;

  const {
    employee,
    startSession,
    endSession,
    startTime,
    endTime,
    leaveType,
    status,
    reason,
    duration,
    excludedDaysNote,
  } = leave;

  const employeeName = getNameFromObj(employee);
  const avatarBg = getColorForText(employeeName, AVATAR_COLORS);
  const initials = `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`;

  const rangeStart = range?.start ?? date;
  const rangeEnd = range?.end ?? rangeStart;
  const isMultiDay = range ? range.start !== range.end : false;

  const statusTone: Tone =
    STATUS_TONE[status as CompanyLeaveStatus] ?? Tone.NEUTRAL;
  const statusLabel = t(`leave_requests.status_map.${status ?? 'pending'}`, {
    defaultValue: getLeaveStatusLabel(status),
  });

  const sessionLabel = (() => {
    if (!startSession) return null;
    if (startSession === endSession || !endSession)
      return getHalfDaySessionLabel(startSession, t);
    return `${getHalfDaySessionLabel(startSession, t)} – ${getHalfDaySessionLabel(endSession, t)}`;
  })();

  return (
    <div className={styles.card}>
      {/* Header: avatar + name/email */}
      <div className={styles.header}>
        <div className={styles.avatar} style={{ background: avatarBg }}>
          {initials}
        </div>
        <div>
          <div className={styles.name}>{employeeName}</div>
          <div className={styles.email}>{employee.email}</div>
        </div>
      </div>

      {/* Metadata grid */}
      <div className={styles.meta}>
        {/* Type */}
        <div>
          <span className={styles.metaLabel}>
            {t('calendar.event_detail.type')}
          </span>
          <span className={cn(styles.typeChip, badgeClass)}>
            {leaveType.name}
          </span>
        </div>

        {/* Status */}
        <div>
          <span className={styles.metaLabel}>
            {t('calendar.event_detail.status')}
          </span>
          <Chip type={statusTone} size="sm">
            {statusLabel}
          </Chip>
        </div>

        {/* Date */}
        <div>
          <span className={styles.metaLabel}>
            {isMultiDay
              ? t('calendar.event_detail.dates')
              : t('calendar.event_detail.date')}
          </span>
          <span className={styles.metaValue}>
            {isMultiDay
              ? `${formatRangeDate(rangeStart)} – ${formatRangeDate(rangeEnd)}`
              : formatRangeDate(rangeStart)}
          </span>
        </div>

        {/* Duration or session/time */}
        {duration ? (
          <div>
            <span className={styles.metaLabel}>
              {t('calendar.event_detail.duration')}
            </span>
            <span className={styles.metaValue}>{duration}</span>
          </div>
        ) : startTime && endTime ? (
          <div>
            <span className={styles.metaLabel}>
              {t('calendar.event_detail.time')}
            </span>
            <span className={styles.metaValue}>
              {startTime}–{endTime}
            </span>
          </div>
        ) : (
          sessionLabel && (
            <div>
              <span className={styles.metaLabel}>
                {t('calendar.event_detail.session')}
              </span>
              <span className={styles.metaValue}>{sessionLabel}</span>
            </div>
          )
        )}
      </div>

      {/* Excluded days notice */}
      {excludedDaysNote && (
        <div className={styles.notice}>
          <CalendarX2
            className={styles.noticeIcon}
            size={14}
            aria-hidden="true"
          />
          <span>{excludedDaysNote}</span>
        </div>
      )}

      {/* Leave reason */}
      {reason && <p className={styles.reason}>&ldquo;{reason}&rdquo;</p>}
    </div>
  );
}
